import { useEffect, useRef, useState } from 'react'
import { FetchPolicy } from '@apollo/client'
import { ApolloQueryResult } from '@apollo/client'
import gql from 'graphql-tag'
import { apolloClient } from '@/state/apollo-client'
import { useFormContext } from '@/state/main-form/reducer'
import {
	TargetedInfoBlockHtmlPollQuery,
	TargetedInfoBlockHtmlPollQueryVariables,
	TargetedInfoBlockHtmlPubPollQuery,
	TargetedInfoBlockHtmlPubPollQueryVariables,
} from './__gen/useTargetedInfoBlockHtmlPoll'

const POLL_INTERVAL_ACTIVE = 10_000
const POLL_INTERVAL_IDLE = 60_000
const IDLE_THRESHOLD = 15 * 60 * 1_000

const TARGETED_INFO_BLOCK_HTML_POLL_QUERY = gql`
	query targetedInfoBlockHtmlPoll($recordId: ID!) {
		targetedInfoBlockHtml_List(record: $recordId) {
			objects {
				id
				infoBlockId
				html
			}
		}
	}
`

const TARGETED_INFO_BLOCK_HTML_PUB_POLL_QUERY = gql`
	query targetedInfoBlockHtmlPubPoll($recordId: ID!) {
		targetedInfoBlockHtmlPub_List(record: $recordId) {
			objects {
				id
				infoBlockId
				html
			}
		}
	}
`

// Polls the targeted (record specific) html for all info blocks on the form.
// Returns a map of infoBlockId -> html for info blocks that have one.
export const useTargetedInfoBlockHtmlPoll = () => {
	const lastActivityRef = useRef(Date.now())
	const timeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
	const formContext = useFormContext()
	const [htmlByInfoBlockId, setHtmlByInfoBlockId] = useState<{ [infoBlockId: string]: string }>({})

	const recordId = formContext.record.id
	const enabled = formContext.form.infoBlocks.some((infoBlock) => infoBlock.hasTargetedHtml)

	useEffect(() => {
		if (!enabled) {
			return
		}
		const updateActivity = () => {
			lastActivityRef.current = Date.now()
		}
		window.addEventListener('mousemove', updateActivity)
		window.addEventListener('keydown', updateActivity)
		window.addEventListener('click', updateActivity)
		return () => {
			window.removeEventListener('mousemove', updateActivity)
			window.removeEventListener('keydown', updateActivity)
			window.removeEventListener('click', updateActivity)
		}
	}, [enabled])

	useEffect(() => {
		if (!enabled) {
			return
		}
		const poll = async () => {
			try {
				let result: ApolloQueryResult<TargetedInfoBlockHtmlPollQuery | TargetedInfoBlockHtmlPubPollQuery>
				const common: { variables: { recordId: string }; fetchPolicy: FetchPolicy } = {
					variables: { recordId },
					fetchPolicy: 'network-only',
				}

				if (formContext.publicForm) {
					result = await apolloClient.query<
						TargetedInfoBlockHtmlPubPollQuery,
						TargetedInfoBlockHtmlPubPollQueryVariables
					>({
						...common,
						query: TARGETED_INFO_BLOCK_HTML_PUB_POLL_QUERY,
						context: {
							headers: formContext.publicForm ? { 'X-Public-Form-Code': formContext.publicForm.code } : {},
						},
					})
				} else {
					result = await apolloClient.query<
						TargetedInfoBlockHtmlPollQuery,
						TargetedInfoBlockHtmlPollQueryVariables
					>({
						...common,
						query: TARGETED_INFO_BLOCK_HTML_POLL_QUERY,
					})
				}

				if (!result.data) {
					console.error('no data')
					return
				}

				const objects =
					'targetedInfoBlockHtml_List' in result.data ?
						result.data.targetedInfoBlockHtml_List?.objects
					:	result.data.targetedInfoBlockHtmlPub_List?.objects

				const next: { [infoBlockId: string]: string } = {}
				for (const obj of objects || []) {
					if (obj) {
						next[obj.infoBlockId] = obj.html
					}
				}
				setHtmlByInfoBlockId(next)
			} catch {
				// silently ignore transient polling errors
			}

			const isIdle = Date.now() - lastActivityRef.current > IDLE_THRESHOLD
			timeoutRef.current = setTimeout(poll, isIdle ? POLL_INTERVAL_IDLE : POLL_INTERVAL_ACTIVE)
		}
		poll()

		return () => {
			if (timeoutRef.current !== null) {
				clearTimeout(timeoutRef.current)
			}
		}
	}, [recordId, enabled])

	return htmlByInfoBlockId
}
